/**
 * MemoryFrame keeps its globe controls visible in the workspace; an opened
 * window keeps only its title and hands Search and Review to its pill, Fit
 * globe to its ⋯. As a
 * Pinned tile (a surface that owns its chrome) it is the live globe under a
 * calm title with "Open memory" and its count line, naming a thing only on
 * hover; a click on the tile outside its controls and the globe opens it in
 * Focus.
 * While the wall searches, the tile's matching things cover the globe, which
 * keeps running underneath so clearing the search shows it at once.
 */

import { useMemo, type MouseEvent } from "react";
import { useSubscriptionStatus } from "@/components/settings/account-plan-state";
import { useWallTileQuery } from "@/components/shapes/shared/wall-tile-query-context";
import { useAuth } from "@/hooks/use-auth";
import { Button, Icons, SurfaceFade } from "@/components/ui";
import { useT } from "@/i18n";
import { framesMemoryMessages } from "@/i18n/messages/frames-memory";
import { useMemoryStore } from "@/stores/memory-store";
import { cn } from "@/lib/cn";
import { MemoryFrameBody, type MemoryAccess } from "./memory-frame-body";
import { useFrameSurface } from "../shared/use-frame-surface";
import type { FrameProps } from "../shared/frame-props";
import {
  useFrameChromeHidden,
  useFrameRenderer,
} from "../shared/frame-renderer-context";
import { FrameCalmTitle } from "../shared/frame-calm-title";
import {
  useFrameChromeConfig,
  type FrameChromeConfig,
} from "../shared/frame-chrome-config";
import { MemorySearchResults } from "./memory-search-results";
import { openProjectMemory } from "./open-project-memory";

/**
 * What keeps its own click on the tile: controls, rows, the pocket, and the
 * globe, whose drags turn it and whose taps pick a thing.
 */
const TILE_OWN_CLICKS = "button, a, input, textarea, canvas, [role='region']";

function openOnTileBackground(event: MouseEvent<HTMLDivElement>): void {
  if (event.target instanceof Element && event.target.closest(TILE_OWN_CLICKS))
    return;
  openProjectMemory(null);
}

/**
 * The tile's search: a matching thing opens the memory frame on it. It shows
 * what fits and fades at the foot; the wheel scrolls the wall.
 */
function MemoryTileSearch({ query }: { query: string }) {
  const t = useT(framesMemoryMessages);
  const { user } = useAuth();
  return (
    <div
      className="absolute inset-0 overflow-hidden px-[0.8em] pb-[0.8em] pt-[0.5em]"
      data-testid="memory-tile-search"
    >
      <MemorySearchResults
        query={query}
        youName={user?.name ?? ""}
        emptyText={t("tileSearchEmpty", { query })}
        onOpen={(thing) => openProjectMemory(thing.id ?? null)}
      />
      <SurfaceFade />
    </div>
  );
}

export function MemoryFrame({ id, data, selected }: FrameProps) {
  const t = useT(framesMemoryMessages);
  const frameSurface = useFrameSurface(id);
  // Keeps the globe sharp when the surface magnifies the frame.
  const backingZoom = useFrameRenderer().useBackingScale();
  const chromeHidden = useFrameChromeHidden();
  const query = useWallTileQuery();
  const reviewCount = useMemoryStore((state) => state.reviewItems.length);
  const { status, failed } = useSubscriptionStatus();
  // Without the knowledge graph the frame never asks for it. An unknown plan
  // loads anyway and lets the globe show its own error.
  const access: MemoryAccess =
    status?.has_pro_access === false
      ? "locked"
      : status || failed
        ? "open"
        : "checking";
  const searching = chromeHidden && access === "open" && query !== "";
  // An opened window's pill hosts Search and Review, its ⋯ Fit globe.
  const chromeConfig = useMemo<FrameChromeConfig>(
    () =>
      access === "locked"
        ? { tools: [] }
        : {
            tools: [
              {
                kind: "button",
                id: "memory-search",
                label: t("searchMemory"),
                icon: Icons.Search,
                onSelect: () => useMemoryStore.getState().openSearch(),
              },
              {
                kind: "button",
                id: "memory-review",
                label:
                  reviewCount > 0
                    ? t("reviewWithCount", { count: reviewCount })
                    : t("review"),
                ariaLabel:
                  reviewCount > 0
                    ? t("reviewSuggestions", { count: reviewCount })
                    : t("nothingToReview"),
                onSelect: () => useMemoryStore.getState().openReview(),
                disabled: reviewCount === 0,
              },
            ],
            menuItems: [
              {
                id: "memory-fit",
                label: t("fitGlobe"),
                icon: Icons.Crosshair,
                onSelect: () => useMemoryStore.getState().fit(),
              },
            ],
          },
    [access, reviewCount, t],
  );
  const opened = useFrameChromeConfig(chromeConfig);

  return (
    <div
      className={cn(
        frameSurface.className,
        "relative flex h-full flex-col overflow-hidden",
        selected
          ? "border-[var(--frame-selected-stroke)]"
          : "border-glass-stroke",
      )}
      data-testid="memory-frame"
      onClick={chromeHidden ? openOnTileBackground : undefined}
      {...frameSurface.attributes}
    >
      {chromeHidden ? (
        // Search, Review and Fit stay in the opened item.
        <FrameCalmTitle
          title={t("memory")}
          action={{
            label: t("openMemory"),
            onClick: () => openProjectMemory(null),
            testId: "wall-pin-open-memory",
          }}
          data-testid="memory-frame-top-row"
        />
      ) : opened ? (
        // The opened window's pill and ⋯ hold the row's controls.
        <FrameCalmTitle
          title={data.title || t("memory")}
          data-testid="memory-frame-top-row"
        />
      ) : (
        <div
          className="relative z-[80] flex shrink-0 items-center gap-2 px-3 pb-2 pt-3"
          data-testid="memory-frame-top-row"
        >
          <span
            className="min-w-0 flex-1 truncate text-appkit-body font-semibold text-primary cursor-grab active:cursor-grabbing"
            data-frame-drag-handle="true"
            data-testid="memory-frame-title"
          >
            {data.title || t("memory")}
          </span>
          {access === "locked" ? null : (
            <>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="nodrag min-w-0 justify-start gap-2 text-muted"
                onClick={() => useMemoryStore.getState().openSearch()}
                data-testid="memory-frame-search"
                aria-label={t("searchMemory")}
              >
                <Icons.Search
                  className="h-3.5 w-3.5 shrink-0"
                  aria-hidden="true"
                />
                <span className="truncate">{t("searchMemory")}</span>
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="nodrag shrink-0"
                disabled={reviewCount === 0}
                onClick={() => useMemoryStore.getState().openReview()}
                data-testid="memory-frame-review"
                aria-label={
                  reviewCount > 0
                    ? t("reviewSuggestions", { count: reviewCount })
                    : t("nothingToReview")
                }
              >
                {reviewCount > 0
                  ? t("reviewWithCount", { count: reviewCount })
                  : t("review")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="nodrag shrink-0"
                onClick={() => useMemoryStore.getState().fit()}
                data-testid="memory-frame-fit"
                aria-label={t("fitGlobe")}
                title={t("fitGlobe")}
              >
                <Icons.Crosshair className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </>
          )}
        </div>
      )}
      {chromeHidden ? (
        <div className="relative flex min-h-0 flex-1 flex-col">
          <div
            className={cn(
              "flex min-h-0 flex-1 flex-col",
              searching && "invisible",
            )}
          >
            <MemoryFrameBody
              frameId={id}
              pixelBoost={backingZoom}
              access={access}
            />
          </div>
          {searching ? <MemoryTileSearch query={query} /> : null}
        </div>
      ) : (
        <MemoryFrameBody
          frameId={id}
          pixelBoost={backingZoom}
          access={access}
        />
      )}
    </div>
  );
}
